import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight,
  MapPin,
  Bike,
  Car,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Sun,
  TrendingUp,
  ChevronDown
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-800 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-900/30 transition-colors"
    >
      <span className="font-medium text-white pr-4">{question}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-6 pb-4 text-gray-400">
        {answer}
      </div>
    )}
  </div>
);

const brisbaneFAQs = [
  {
    q: "How much do food delivery riders earn in Brisbane?",
    a: "Brisbane delivery riders typically earn $18-$38 per hour. E-bike riders in inner Brisbane average $18-$26/hr, scooter riders earn $22-$34/hr, and car drivers covering outer suburbs like Logan can make $24-$38/hr. Brisbane's lower cost of living means your earnings go further compared to Sydney or Melbourne."
  },
  {
    q: "What are the best areas for delivery in Brisbane?",
    a: "Top performing areas include Brisbane CBD (weekday lunch demand), Fortitude Valley (entertainment district, late-night orders), South Brisbane and West End (trendy dining, consistent orders), Chermside and Aspley (suburban shopping hubs), and Sunnybank (large Asian food precinct with loyal customers). The CBD and inner suburbs work well for all vehicle types."
  },
  {
    q: "Can I ride a 50cc scooter with a car licence in Brisbane?",
    a: "Yes! Queensland is unique in Australia — you can legally ride scooters up to 50cc with just a standard car licence (C class). This makes scooter delivery much more accessible in Brisbane compared to Sydney or Melbourne where a motorcycle licence is required for any scooter. For larger scooters over 50cc, you'll need an RE licence."
  },
  {
    q: "How do I become an Uber Eats or DoorDash rider in Brisbane?",
    a: "To start in Brisbane you need: valid photo ID, proof of work rights (visa or citizenship), Australian bank account, smartphone, and a vehicle. Sign up through the platform's app, submit your documents, and wait for verification (1-5 days). A police check may be required. Just Delivery guides you through each step to get started faster."
  },
  {
    q: "Is Brisbane a good city for new delivery riders?",
    a: "Brisbane is excellent for new riders. It's one of Australia's fastest-growing cities with increasing delivery demand and less competition than Sydney or Melbourne. The subtropical climate means comfortable year-round riding, and the 50cc scooter car-licence rule makes it easier to start. Lower living costs also mean your earnings stretch further."
  },
  {
    q: "What should I know about delivering in Brisbane's summer?",
    a: "Brisbane summers (December-February) are hot and humid, often reaching 30-35°C. Stay hydrated, wear breathable clothing, and take regular breaks in air-conditioned spaces. Consider working early mornings (7-11am) or evenings (5-9pm) to avoid peak heat. Car delivery with air conditioning is popular in summer, though demand for all riders remains strong."
  }
];

export default function BrisbaneGuide() {
  const [openFAQ, setOpenFAQ] = useState(null);

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": brisbaneFAQs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#6ABF4B]/10 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Link to="/how-to-start-delivery-australia" className="inline-flex items-center gap-2 text-[#6ABF4B] hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Australia Guide
          </Link>
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#6ABF4B]" />
              <span className="text-[#6ABF4B] font-medium">BRISBANE DELIVERY GUIDE 2026</span>
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              How to Start <span className="text-[#6ABF4B]">Delivery Work</span> in Brisbane
            </motion.h1>
            
            <motion.p variants={fadeInUp} className="text-xl text-gray-400 mb-8 max-w-2xl">
              Your complete guide to food delivery in Brisbane — Queensland's sunny capital with year-round riding weather and a rapidly growing delivery market.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link
                to="/?lang=en#packages"
                className="inline-flex items-center gap-2 bg-[#6ABF4B] text-black px-6 py-3 rounded-lg font-semibold hover:bg-[#5aa83f] transition-colors"
              >
                Get Started in Brisbane <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Brisbane Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Why Brisbane is Great for Delivery Riders</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Sun className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Perfect Riding Weather</h3>
              <p className="text-gray-400">Brisbane's subtropical climate means you can work outdoors comfortably year-round. Sunny days are the norm, making bike and scooter delivery enjoyable.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Rapidly Growing Market</h3>
              <p className="text-gray-400">Brisbane is one of Australia's fastest-growing cities. More residents means more delivery demand, and the market is less saturated than Sydney or Melbourne.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Lower Living Costs</h3>
              <p className="text-gray-400">Compared to Sydney and Melbourne, Brisbane has lower rent and living expenses. Your delivery earnings go further here.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Less Competition</h3>
              <p className="text-gray-400">With fewer riders competing for orders, you're more likely to stay busy during peak hours and can build regular customers faster.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Areas */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Best Areas for Delivery in Brisbane</h2>
          <p className="text-gray-400 mb-8">These areas have the highest order volumes and best earning potential:</p>
          
          <div className="space-y-4">
            {[
              { area: "Brisbane CBD", desc: "High concentration of restaurants and office workers. Strong lunch demand on weekdays." },
              { area: "Fortitude Valley", desc: "Brisbane's entertainment hub with bars, restaurants, and late-night orders." },
              { area: "South Brisbane & West End", desc: "Trendy dining precinct with diverse cuisine and consistent orders." },
              { area: "Chermside & Aspley", desc: "Major suburban shopping hub with steady residential demand." },
              { area: "Sunnybank", desc: "Large Asian food precinct with unique delivery opportunities and loyal customers." },
            ].map((item, idx) => (
              <div key={idx} className="bg-[#111111] border border-gray-800 rounded-xl p-5 flex items-start gap-4">
                <div className="w-8 h-8 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-bold text-sm">{idx + 1}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-white">{item.area}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Typical Brisbane Delivery Earnings</h2>
          <p className="text-gray-400 mb-8">Earnings vary based on vehicle type, hours worked, and delivery zones:</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Bike className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">E-Bike</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$18 – $26/hr</p>
              <p className="text-gray-400 text-sm">Great for inner Brisbane. Weather is rarely a problem. No licence needed.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Bike className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Scooter</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$22 – $34/hr</p>
              <p className="text-gray-400 text-sm">50cc scooters allowed with car licence in QLD. Great for covering more suburbs.</p>
            </div>
            
            <div className="bg-black border border-gray-800 rounded-xl p-6 text-center">
              <Car className="w-10 h-10 text-[#6ABF4B] mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Car</h3>
              <p className="text-2xl font-bold text-[#6ABF4B] mb-2">$24 – $38/hr</p>
              <p className="text-gray-400 text-sm">Best for outer suburbs like Logan and Ipswich. Air conditioning is a plus in summer.</p>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm mt-6 text-center">
            * Earnings are estimates based on typical Brisbane delivery data. Actual results vary by demand, time, and experience.
          </p>
        </div>
      </section>

      {/* Brisbane-Specific Requirements */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Brisbane-Specific Requirements</h2>
          <p className="text-gray-400 mb-8">Queensland has different rules compared to NSW and VIC:</p>
          
          <div className="space-y-4">
            <div className="bg-[#111111] border border-[#6ABF4B]/50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#6ABF4B] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">50cc Scooter with Car Licence</h3>
                  <p className="text-gray-400">Unlike NSW and VIC, Queensland allows you to ride scooters up to 50cc with just a standard car licence (C class). This makes scooter delivery more accessible in Brisbane.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Larger Scooters Need RE Licence</h3>
                  <p className="text-gray-400">For scooters over 50cc, you'll need an RE (Restricted) motorcycle licence. Consider starting with a 50cc scooter if you only have a car licence.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-[#6ABF4B] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">E-Bike Regulations</h3>
                  <p className="text-gray-400">Same as other states: e-bikes must be pedal-assist only (no throttle) with maximum 250W power. No licence required for compliant e-bikes.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <FileText className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Overseas Licence Conversion</h3>
                  <p className="text-gray-400">Visit Transport and Main Roads (201 Charlotte St, Brisbane CBD) to convert your overseas licence. QLD has agreements with many countries for direct conversion.</p>
                </div>
              </div>
            </div>

            <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Sun className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-white mb-2">Summer Heat Considerations</h3>
                  <p className="text-gray-400">Brisbane summers are hot and humid. Stay hydrated, take breaks, and consider working early mornings or evenings during peak summer months.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#111111]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Delivering in Brisbane?</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Just Delivery helps you navigate the onboarding process for Uber Eats and DoorDash. We review your documents, guide you through requirements, and help you start earning faster.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/?lang=en#packages"
              className="inline-flex items-center gap-2 bg-[#6ABF4B] text-black px-8 py-4 rounded-lg font-semibold hover:bg-[#5aa83f] transition-colors"
            >
              Start Your Application <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/how-to-start-delivery-australia"
              className="inline-flex items-center gap-2 bg-transparent border border-gray-600 text-white px-8 py-4 rounded-lg font-semibold hover:border-[#6ABF4B] transition-colors"
            >
              Read Full Australia Guide
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-center">Frequently Asked Questions</h2>
          <p className="text-gray-400 mb-8 text-center">Common questions about delivery work in Brisbane</p>
          
          <div className="bg-[#111111] border border-gray-800 rounded-xl overflow-hidden">
            {brisbaneFAQs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.q}
                answer={faq.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Other Cities Links */}
      <section className="py-12 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold mb-6 text-center">Delivery Guides for Other Cities</h3>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/how-to-start-delivery-sydney"
              className="text-[#6ABF4B] hover:underline flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Sydney Guide
            </Link>
            <span className="text-gray-600">|</span>
            <Link
              to="/how-to-start-delivery-melbourne"
              className="text-[#6ABF4B] hover:underline flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" /> Melbourne Guide
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-black border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-gray-500 text-sm text-center">
            Just Delivery is not affiliated with Uber Eats, DoorDash, or any delivery platform. Platform approval decisions are made solely by the respective platforms. We provide onboarding guidance only.
          </p>
        </div>
      </section>
    </div>
    </>
  );
}
